import { Component, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';

// Angular material components
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

// Custom services 
import { LanguageService } from '../../../services/language/language.service';
import { UtilsService } from '../../../services/utils/utils.service';

import { UntypedFormControl, FormGroupDirective, NgForm, Validators, AbstractControl, FormBuilder } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';

/* Error when invalid control is dirty, touched, or submitted (input fields) */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched ));
  }
}

/* Specify the structure of the dictionary that will receive the dialog when being openned */
export interface DialogData {additionalInformationDict:any}


@Component({
  selector: 'app-additional-data-popup',
  standalone: true,
  imports: [
    // Angular modules
    CommonModule, 
    // Material design modules
    MatIconModule,
    MatButtonModule,
    MatRadioModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    // Custom modules
  ],
  templateUrl: './additional-data-popup.component.html',
  styleUrl: './additional-data-popup.component.css'
})
export class AdditionalDataPopupComponent {

  additionalInformationDict:any = null
  additionalInformationDictSaved:any = null
  shouldUpdateAdditionalData:boolean = null

  ageDict = {minValue: 0, maxValue: 150, step: 1}
  weightDict = {minValue: 0, maxValue: 300, step: 1}
  heightDict = {minValue: 0.2, maxValue: 3, step: 0.01}
  sexValueList = ['M', 'F', 'O']
  sexValue = null 

  // Individual form controls and error matcher
  numericFormControlAge = new UntypedFormControl('', [
    Validators.max(this.ageDict.maxValue),
    Validators.min(this.ageDict.minValue),
  ]); 

  numericFormControlWeight = new UntypedFormControl('', [
    Validators.max(this.weightDict.maxValue),
    Validators.min(this.weightDict.minValue),
  ]); 

  numericFormControlHeight = new UntypedFormControl('', [
    Validators.max(this.heightDict.maxValue),
    Validators.min(this.heightDict.minValue),
  ]); 

  categoricalFormControlSex = new UntypedFormControl('', []);

  matcher = new MyErrorStateMatcher();

  // Form control group (need to keep this name cause its binded to parents form control)
  //    It needs to be created before constructing and initializing the view  
  formControlGroup = this._formBuilder.group({
    age: this.numericFormControlAge,
    weight: this.numericFormControlWeight,
    height: this.numericFormControlHeight,
    sex: this.categoricalFormControlSex,
  }, 
  {});

  constructor(private _formBuilder:FormBuilder,
    public languageService:LanguageService,
    public utilsService:UtilsService,
    public dialogRef: MatDialogRef<AdditionalDataPopupComponent>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: DialogData){

      this.additionalInformationDict = this.utilsService.deepCopyDict(data.additionalInformationDict)
      this.additionalInformationDictSaved = this.utilsService.deepCopyDict(data.additionalInformationDict)
      this.shouldUpdateAdditionalData = false
    }

  ngOnInit(): void {
    // Initialize variables
    //    Age
    this.numericFormControlAge.setValue(null)
    if('PatientAge' in this.additionalInformationDict){
      let ageValue = Number(this.additionalInformationDict['PatientAge'])
      this.numericFormControlAge.setValue(ageValue)
    }
    //    Weight
    this.numericFormControlWeight.setValue(null)
    if('PatientWeight' in this.additionalInformationDict){
      let weightValue = Number(this.additionalInformationDict['PatientWeight'])
      this.numericFormControlWeight.setValue(weightValue)
    }
    //    Height
    this.numericFormControlHeight.setValue(null)
    if('PatientHeight' in this.additionalInformationDict){
      let heightValue = Number(this.additionalInformationDict['PatientHeight'])
      this.numericFormControlHeight.setValue(heightValue)
    }
    //    Sex
    this.sexValue = null
    if('PatientSex' in this.additionalInformationDict){
      this.sexValue = this.sexValueList.indexOf(this.additionalInformationDict['PatientSex']); 
      if(this.sexValue == 0 || this.sexValue == 1 || this.sexValue == 2 ){
        this.categoricalFormControlSex.setValue(this.sexValue)
      }
    }
  }


  // --- Button actions --- //


  exitButtonClicked(){
    let unsavedChanges = this.evaluateUnsavedChanges()
    if(!unsavedChanges){
      this.dialogRef.close({
        shouldUpdateAdditionalData:this.shouldUpdateAdditionalData, 
        additionalInformationDict:this.additionalInformationDict})
    } else {
      this.utilsService.displayDecisionPopup(
        this.languageService.Strings.popups.titleSaveChanges,
        this.languageService.Strings.popups.textSaveChanges,
        this.shouldSaveBeforeExiting.bind(this))
    }
  }

  shouldSaveBeforeExiting(shouldSave){
    if(shouldSave) this.saveChanges();
    this.dialogRef.close({shouldUpdateAdditionalData:this.shouldUpdateAdditionalData, additionalInformationDict:this.additionalInformationDict})
  }

  saveButtonClicked(){
    this.saveChanges()
    this.utilsService.displaySnackBar(this.languageService.Strings.feedbackStrings.successfullySaved);
  }

  keyboardPress(event){
    // Scape pressed (27)
    if(event.keyCode == 27) {
      event.preventDefault();
      return false
    }
    return true
  }

  keyboardUp(event){
    // Scape pressed (27)
    if(event.keyCode == 27) {
      event.preventDefault();
      this.exitButtonClicked()
    }
    return true
  }

  getRadioButtonIsChecked(index){
   if(index == this.sexValue) return true
   return false
  }

  radioButtonClicked(event:any, currentButton:any){
    // Toggle del check-box
    const savedValue = this.categoricalFormControlSex.value;
    const currentValue = currentButton.value;

    if(savedValue === currentValue){
      this.categoricalFormControlSex.setValue(null);
    } else {
      this.categoricalFormControlSex.setValue(savedValue);
    }
  }


  // --- Internal functions --- //


  saveChanges(){
    /* 
    Si ha escrito un valor en el campo, lo actualizamos en el diccionario y, si lo ha dejado en blanco, 
    eliminamos el elemento del diccionario.
    */
    // Age
    if(this.numericFormControlAge.value){
      this.additionalInformationDict['PatientAge'] =  this.numericFormControlAge.value.toString()
    } else if (this.numericFormControlAge.value == null){
      delete this.additionalInformationDict['PatientAge'];
    }
    // Weight
    if(this.numericFormControlWeight.value){
      this.additionalInformationDict['PatientWeight'] =  this.numericFormControlWeight.value
    } else if (this.numericFormControlWeight.value == null){
      delete this.additionalInformationDict['PatientWeight'];
    }
    // Height
    if(this.numericFormControlHeight.value){
      this.additionalInformationDict['PatientHeight'] =  this.numericFormControlHeight.value
    } else if (this.numericFormControlHeight.value == null){
      delete this.additionalInformationDict['PatientHeight'];
    }
    // Sex
    let sexValue = this.categoricalFormControlSex.value
    if(sexValue == 0 || sexValue == 1 || sexValue == 2 ){
      this.additionalInformationDict['PatientSex'] = this.sexValueList[sexValue]
    } else if (this.categoricalFormControlSex.value == null){
      delete this.additionalInformationDict['PatientSex'];
    }
    // Notify that there are updates
    this.shouldUpdateAdditionalData = true
  }

  evaluateUnsavedChanges(){
    // Age
    if(this.numericFormControlAge.value != null){
      if(!('PatientAge' in this.additionalInformationDict)) return true
      if(this.numericFormControlAge.value.toString() != this.additionalInformationDict['PatientAge']) return true
    }
    // Weight
    if(this.numericFormControlWeight.value != null){
      if(!('PatientWeight' in this.additionalInformationDict)) return true
      if(this.numericFormControlWeight.value != this.additionalInformationDict['PatientWeight']) return true
    }
    // Height
    if(this.numericFormControlHeight.value != null){
      if(!('PatientHeight' in this.additionalInformationDict)) return true
      if(this.numericFormControlHeight.value != this.additionalInformationDict['PatientHeight']) return true
    }
    // Sex
    let sexValue = this.categoricalFormControlSex.value
    if(sexValue === 0 || sexValue === 1 || sexValue === 2 ){
      if(!('PatientSex' in this.additionalInformationDict)) return true
      if(this.sexValueList[sexValue] != this.additionalInformationDict['PatientSex']) return true
    }
    // Return false if there are not unsaved changes
    return false
  }

}
